/**
 * Locale + namespace registry constants for `@4f/i18n`.
 *
 * A **locale** is a language the app ships strings for; a **namespace** is the
 * unit of lazy loading (one per surface/feature). Detection resolves a browser
 * preference list down to a supported locale; the loader lazy-loads a single
 * `(locale, namespace)` pair with a fallback chain to {@link DEFAULT_LOCALE}.
 */

/**
 * Every locale the product can resolve to. Base (region-less) tags only.
 *
 * v1 ships `en` only — the loader/detector/registry are already
 * locale-plural (add a locale = add a registry entry + `locales/<tag>/*`, not
 * a conditional), but no other locale has translated message trees yet.
 * Don't claim a language the product doesn't speak: adding a tag here without
 * shipping its `locales/<tag>/*` + registry entry lies to users (`<html
 * lang>`, `og:locale`, hreflang) and 404s namespace loads at runtime.
 */
export const SUPPORTED_LOCALES = ['en'] as const;

/** The locale every fallback chain terminates at — must be fully translated. */
export const DEFAULT_LOCALE = 'en' as const;

/** Lazy-loading units. Add a surface = add a namespace, not a conditional. */
export const NAMESPACES = [
  'common',
  'errors',
  'chat',
  'builder',
  'billing',
  'referral',
  'gallery',
  'admin',
  'surface',
  'settings',
  'auth',
] as const;

/** A supported language tag (e.g. `en`). */
export type Locale = (typeof SUPPORTED_LOCALES)[number];

/** A loadable message namespace (e.g. `common`). */
export type Namespace = (typeof NAMESPACES)[number];

/**
 * Whether the product speaks more than one language. A language picker that
 * offers exactly one choice is a control that cannot do anything — surfaces
 * gate on this instead of rendering a dead `<select>`. It flips to `true` the
 * moment a second locale lands, with no component change.
 */
export const HAS_LOCALE_CHOICE: boolean = SUPPORTED_LOCALES.length > 1;

/** Narrowing guard — is `value` one of the supported locales? */
export function isSupportedLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}
